/**
 * Kazakhstan phone normalization to E.164 `+7XXXXXXXXXX`, plus the live input
 * mask (`+7 (XXX) XXX-XX-XX`) used by the apply form's phone field — pattern
 * mirrored from the equivalent lead-form phone input in the `paw.kz` repo
 * (`src/components/leadFormHelpers.ts`: `extractPhoneDigits` /
 * `formatPhoneFromDigits` / `applyPhoneInput`), adapted to this app's
 * conventions.
 *
 * Accepts human input with a leading `8` or `+7`, spaces, dashes, and
 * parentheses, and normalizes to the contract format `^\+7[0-9]{10}$`.
 *
 * NOTE (residual, PLI-70/72): the authoritative phone is expected to come from
 * the verified lead identity (OTP), not the form payload. The OTP decision is
 * under review, so the form still collects the phone and this normalizer only
 * hardens the format. Binding the phone to a verified identity is out of scope
 * here and must not be finalized until the OTP/auth model is settled.
 */

export const E164_KZ_PATTERN = /^\+7[0-9]{10}$/;

const PHONE_MASK_PREFIX = '+7';

/** Length of the fully-formatted mask `+7 (700) 123-45-67`, for the input's `maxLength`. */
export const PHONE_MASK_MAX_LENGTH = 18;

/**
 * Extracts up to 10 local digits from raw (possibly partially-masked) input,
 * stripping a leading `+7`/`+8` or an 11-digit `7`/`8` trunk prefix. Used only
 * by the input mask below — silently truncates overflow input rather than
 * rejecting it, which is the expected behavior of a mask (typing or pasting
 * past capacity just stops accepting digits).
 */
export function extractPhoneDigits(value: string): string {
  const trimmed = value.trim();
  let digits: string;

  if (trimmed.startsWith('+7') || trimmed.startsWith('+8')) {
    digits = trimmed.slice(2).replace(/\D/g, '');
  } else {
    digits = trimmed.replace(/\D/g, '');
    if (digits.length >= 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
      digits = digits.slice(1);
    }
  }

  return digits.slice(0, 10);
}

/** Formats local digits as `+7 (XXX) XXX-XX-XX`; empty until the first digit. */
export function formatPhoneFromDigits(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length === 0) return '';

  let result = `${PHONE_MASK_PREFIX} (${d.slice(0, 3)}`;
  if (d.length < 3) return result;

  result += ')';
  if (d.length === 3) return result;

  result += ` ${d.slice(3, 6)}`;
  if (d.length > 6) result += `-${d.slice(6, 8)}`;
  if (d.length > 8) result += `-${d.slice(8, 10)}`;
  return result;
}

/**
 * Applies the mask to a controlled input's next raw value, given its previous
 * (already-masked) value. Treats backspacing over a formatting character
 * (space/paren/dash) as deleting the digit before it, rather than a no-op.
 *
 * The mask expects the user to type the 10 local digits only — the `+7`
 * prefix renders automatically once the first digit lands. Pasting (or
 * autofilling) a fully `+7`/`8`-prefixed number in one operation is parsed
 * correctly, since extraction sees the whole string at once; typing that
 * prefix key by key is not specially recognized (there is no local digit yet
 * to anchor it to), matching the equivalent `paw.kz` lead-form mask.
 */
export function applyPhoneInput(previous: string, nextRaw: string): string {
  const prevDigits = extractPhoneDigits(previous);
  let nextDigits = extractPhoneDigits(nextRaw);

  if (
    nextRaw.length < previous.length &&
    nextDigits.length >= prevDigits.length &&
    prevDigits.length > 0
  ) {
    nextDigits = prevDigits.slice(0, -1);
  }

  return formatPhoneFromDigits(nextDigits);
}

/**
 * Normalizes raw input to `+7XXXXXXXXXX`, or returns `null` when the digits
 * cannot form a valid 10-digit national number.
 */
export function normalizeKzPhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');

  let national: string;
  if (digits.length === 11 && (digits[0] === '8' || digits[0] === '7')) {
    // Local `8XXXXXXXXXX` or already `7XXXXXXXXXX`.
    national = digits.slice(1);
  } else if (digits.length === 10) {
    // Bare national number without country/trunk prefix.
    national = digits;
  } else {
    return null;
  }

  const normalized = `+7${national}`;
  return E164_KZ_PATTERN.test(normalized) ? normalized : null;
}
